import { useState, useEffect, useRef } from 'react';
import './style.scss';
import ClearButtonComponent from '../../Shared/ClearButton';
import { TranslationService } from '../../../services/translation.service';
import { useTexts } from '../../../hooks/texts.hook';
import { type IMunicipality } from '../../../models/municipality.model';
import { type IAreaTicket } from '../../../models/area-ticket-model';
import AreaTicket from '../AreaTicket';
import { LanguageService } from '../../../services/language.service';
import MunicipalitySelector, { type IMunicipalitySelector } from '../../Shared/MunicipalitySelector';
import { AnalyticsService } from '../../../services/analytics-service';
import MunicipalityApiModel from '../../Shared/MunicipalityAutocomplete/models/municipality.api-model';
import Alert from '../../Shared/Alert';
import { SanitizerService } from '../../../services/sanitizer.service';
import AreaTicketApiModel from '../AreaTicket/models/area-ticket.api-model';
import Spinner from '../../Shared/Spinner';
import { MunicipalityHelper } from '../../../helpers/municipality.helper';
import useFetch, { Status } from '../../../hooks/fetch.hook';

enum TicketRecommendationType {
  recommended = 0,
  other = 1,
}

async function getMunicipalitiesByGids(gids: string[]): Promise<IMunicipality[]> {
  const response = await fetch(`/api/areatickets/municipalities?${gids.map((m) => `gids=${m}`).join('&')}`);
  if (!response.ok) throw new Error(response.statusText);

  const result: IMunicipality[] = await response.json();
  return result.map(MunicipalityApiModel.mapToBusinessModel);
}

function createResourcePath(type: string, municipalities: IMunicipality[], language: string): string {
  const municipalitiesParameters = municipalities.map((m) => `municipalityGids=${m.gid}`).join('&');

  switch (type) {
    case 'dayticket':
      return `daytickets?${municipalitiesParameters}&language=${language}`;
    case 'periodticket':
      return `periodtickets?${municipalitiesParameters}&language=${language}`;
    default:
      throw new Error(`Unsupported product type '${type}'`);
  }
}

function processReceivedData(data: AreaTicketApiModel[]): IAreaTicket[] {
  return data?.map((at) => AreaTicketApiModel.mapToBusinessModel(at)) ?? [];
}

function AreaTicketSearch({ productType }: { productType: string }) {
  const { t } = useTexts();

  // === CONSTANTS ===
  const translationPrimaryResultsHeading = TranslationService.get('areaticketsearchpage.primaryresultsheading');
  const translationSecondaryResultsHeading = TranslationService.get('areaticketsearchpage.secondaryresultsheading');
  const currentLanguage = LanguageService.getCurrentLanguage();

  // === STATE ===
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<IMunicipality[]>([]);
  const [selectedTicketRecommendationType, setSelectedTicketRecommendationType] = useState(TicketRecommendationType.recommended);
  const preventFetch = selectedMunicipalities.length === 0;
  const url = !preventFetch ? `/api/areatickets/${createResourcePath(productType, selectedMunicipalities, currentLanguage)}` : undefined;
  const [{ status: getMatchingAreaTicketsDetailsStatus, data: getMatchingAreaTicketsDetailsData }] = useFetch<AreaTicketApiModel[]>(url, preventFetch);

  // === VARIABLES ===
  const matchingAreaTickets = processReceivedData(getMatchingAreaTicketsDetailsData ?? []);

  // === REFS ===
  const municipalitySelectorRef = useRef<IMunicipalitySelector>(null);

  // === EFFECTS ===
  useEffect(() => {
    const municipalityGids = MunicipalityHelper.parseQueryString();
    if (municipalityGids.length === 0) {
      return;
    }

    async function fetchData() {
      try {
        const result = await getMunicipalitiesByGids(municipalityGids);
        setSelectedMunicipalities(result);
      } catch {
        AnalyticsService.addEvent(AnalyticsService.FindTicketCategory, AnalyticsService.ErrorAction, `Hämta kommuner - ${AnalyticsService.InternalErrorLabel}`);
      }
    }
    fetchData();
  }, []);

  // === EVENT HANDLERS ===
  function handleMunicipalitySelectorChange(municipalities: IMunicipality[]): void {
    if (municipalities?.length > 0) {
      setSelectedMunicipalities(municipalities);
    } else {
      setSelectedMunicipalities([]);
    }
    MunicipalityHelper.updateQueryString(municipalities);
    if (selectedMunicipalities.length !== 0) {
      AnalyticsService.addEvent(AnalyticsService.FindTicketCategory, 'Sökalternativ - Kommun', selectedMunicipalities.map((m) => m.name).toString());
    }
  }

  function handleCategoryChange(value: TicketRecommendationType) {
    setSelectedTicketRecommendationType(value);
  }

  function handleClearClick(): void {
    municipalitySelectorRef.current?.clear();
    setSelectedMunicipalities([]);
    setSelectedTicketRecommendationType(TicketRecommendationType.recommended);
    MunicipalityHelper.updateQueryString([]);
    AnalyticsService.addEvent(AnalyticsService.FindTicketCategory, 'Klick på återställ', '');
  }

  // === HTML RENDER HELPERS ===
  const recommendedAreaTicket = matchingAreaTickets.length > 0 ? matchingAreaTickets[0] : undefined;
  const similarAreaTickets = matchingAreaTickets.length > 1 ? matchingAreaTickets.slice(1) : undefined;

  function screenReaderSearchStatus(): string {
    switch (getMatchingAreaTicketsDetailsStatus) {
      case Status.failed:
        return t.generalError;
      case Status.loading:
        return '';
      default:
        return selectedTicketRecommendationType === TicketRecommendationType.recommended
          ? `${TranslationService.get('common.showingxresults').replace('{0}', recommendedAreaTicket ? '1' : '0')} - ${translationPrimaryResultsHeading}`
          : `${TranslationService.get('common.showingxresults').replace('{0}', similarAreaTickets?.length.toString() ?? '0')} - ${translationSecondaryResultsHeading}`;
    }
  }

  return (
    <div className="area-tickets" role="search" aria-label={TranslationService.get('areaticketsearchpage.searchlabel')}>
      <span className="visually-hidden" aria-live="assertive">
        {screenReaderSearchStatus()}
      </span>

      <div className="area-tickets__filters shadow-sm rounded p-sm-3 mb-4">
        <div className="mb-3">
          <label id="area-tickets-municipality-label" className="h3 mb-2" htmlFor="municipality-selector">
            {TranslationService.get('areaticketsearchpage.municipalitylabel')}
          </label>
          {TranslationService.get('common.filterupdatemessage') && <div className="bg-gray p-3 mb-3">{TranslationService.get('common.filterupdatemessage')}</div>}
          <MunicipalitySelector
            id="municipality-selector"
            ref={municipalitySelectorRef}
            placeholderText={TranslationService.get('areaticketsearchpage.municipalityplaceholdertext')}
            noResultsText={TranslationService.get('areaticketsearchpage.municipalitynoresultstext') || TranslationService.get('common.autocomplete.noresults.default')}
            onChange={handleMunicipalitySelectorChange}
            selectedMunicipalities={selectedMunicipalities}
          />
        </div>
        <fieldset>
          <legend className="h3">{TranslationService.get('areaticketsearchpage.ticketrecommendationtypelabel')}</legend>
          <div className="d-flex flex-wrap align-items-center">
            <div className="form-check mb-3 me-3">
              <input
                id="ticket-recommendation-type-recommended"
                type="radio"
                className="form-check-input"
                name="ticket-recommendation-type"
                value={TicketRecommendationType.recommended}
                checked={selectedTicketRecommendationType === TicketRecommendationType.recommended}
                onChange={() => handleCategoryChange(TicketRecommendationType.recommended)}
              />
              <label className="form-check-label fw-normal" htmlFor="ticket-recommendation-type-recommended">
                {translationPrimaryResultsHeading}
              </label>
            </div>
            <div className="form-check mb-3">
              <input
                id="ticket-recommendation-type-other"
                type="radio"
                className="form-check-input"
                name="ticket-recommendation-type"
                value={TicketRecommendationType.other}
                checked={selectedTicketRecommendationType === TicketRecommendationType.other}
                onChange={() => handleCategoryChange(TicketRecommendationType.other)}
              />
              <label className="form-check-label fw-normal" htmlFor="ticket-recommendation-type-other">
                {translationSecondaryResultsHeading}
              </label>
            </div>
          </div>
        </fieldset>
        <div>
          <ClearButtonComponent onClick={handleClearClick} />
        </div>
      </div>
      <section>
        <h2>{selectedTicketRecommendationType === TicketRecommendationType.recommended ? translationPrimaryResultsHeading : translationSecondaryResultsHeading}</h2>
        <div aria-live="assertive">
          {getMatchingAreaTicketsDetailsStatus === Status.notperformed && (
            <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('areaticketsearchpage.nofilterselectedtext')) }} />
          )}
          {getMatchingAreaTicketsDetailsStatus === Status.success && (!recommendedAreaTicket || !similarAreaTickets || similarAreaTickets.length === 0) && (
            <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('areaticketsearchpage.noticketsfoundtext')) }} />
          )}
        </div>

        {getMatchingAreaTicketsDetailsStatus === Status.loading && <Spinner />}
        {getMatchingAreaTicketsDetailsStatus === Status.success && (
          <>
            {selectedMunicipalities.length === 0 && <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('areaticketsearchpage.nofilterselectedtext')) }} />}
            {selectedTicketRecommendationType === TicketRecommendationType.recommended && recommendedAreaTicket && (
              <AreaTicket areaTicket={recommendedAreaTicket} searchedMunicipalities={selectedMunicipalities} />
            )}
            {selectedTicketRecommendationType === TicketRecommendationType.other && similarAreaTickets && similarAreaTickets.length > 0 && (
              <ol className="list-unstyled">
                {similarAreaTickets.map((areaTicket, index) => (
                  <li key={areaTicket.name} className={index < similarAreaTickets.length - 1 ? 'mb-3' : ''}>
                    <AreaTicket areaTicket={areaTicket} searchedMunicipalities={selectedMunicipalities} />
                  </li>
                ))}
              </ol>
            )}
          </>
        )}
      </section>
      <Alert show={getMatchingAreaTicketsDetailsStatus === Status.failed} heading={t.generalError} closable={false} />
    </div>
  );
}

export default AreaTicketSearch;
